import { AdminMemberResponseDto, CurrentMemberResponseDto, MemberInfoResponseDto, MemberUpdateStatus, } from '@lucidlink/dto/web-service-api';
import { arrayParserFromDto, parserFromDto } from '#fetcher/parse-utils';
import { prepareUpdateRole } from '#sdk/common/member/prepare-update-role.ts';
import { remapInvitationResponse } from '../invitation-manager/remap-invitation-response';
export const createMemberManager = ({ workspaceFetcher, membersMap, getAdminCrypto, getPrincipalsCrypto, loginToWorkspace, }) => {
    return new MemberManagerImpl(workspaceFetcher, membersMap, getAdminCrypto, getPrincipalsCrypto, loginToWorkspace);
};
class MemberManagerImpl {
    workspaceFetcher;
    membersMap;
    getAdminCrypto;
    getPrincipalsCrypto;
    loginToWorkspace;
    membersFetcher;
    constructor(workspaceFetcher, membersMap, getAdminCrypto, getPrincipalsCrypto, loginToWorkspace) {
        this.workspaceFetcher = workspaceFetcher;
        this.membersMap = membersMap;
        this.getAdminCrypto = getAdminCrypto;
        this.getPrincipalsCrypto = getPrincipalsCrypto;
        this.loginToWorkspace = loginToWorkspace;
        this.membersFetcher = this.workspaceFetcher.createSubFetcher({
            path: 'members',
        });
    }
    async getCurrentMember() {
        await this.loginToWorkspace();
        return await this.membersFetcher.get('me', {
            parser: parserFromDto(CurrentMemberResponseDto),
        });
    }
    async getAdminMember() {
        await this.loginToWorkspace();
        return await this.membersFetcher.get('admin', {
            parser: parserFromDto(AdminMemberResponseDto),
        });
    }
    async getMember(memberId) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const member = await this.membersFetcher.get(memberId, {
            parser: parserFromDto(MemberInfoResponseDto),
        });
        return await remapMember(member, adminCrypto);
    }
    async listMembers(includeGroups) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const members = await this.membersFetcher.get([], {
            queryParams: { includeGroups, includeGuests: true },
            parser: arrayParserFromDto(MemberInfoResponseDto),
        });
        return await Promise.all(members.map((member) => remapMember(member, adminCrypto)));
    }
    async changeRole(params) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const principalsCrypto = await this.getPrincipalsCrypto();
        const updateDto = await prepareUpdateRole({
            params,
            principalsCrypto,
            adminCrypto,
        });
        const member = await this.membersFetcher.patch([params.memberId, 'role'], {
            body: updateDto,
            parser: parserFromDto(MemberInfoResponseDto),
        });
        return await remapMember(member, adminCrypto);
    }
    async removeMember(memberId) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.membersFetcher.delete(memberId);
        (await this.getPrincipalsCrypto()).refresh();
        await this.membersMap.refresh();
    }
    async requestRemoveMember(memberId) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.membersFetcher.post([memberId, 'remove-request']);
    }
    async deactivate(memberId) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const member = await this.membersFetcher.patch([memberId, 'status'], {
            body: { status: MemberUpdateStatus.DEACTIVATED },
            parser: parserFromDto(MemberInfoResponseDto),
        });
        return await remapMember(member, adminCrypto);
    }
    async reactivate(memberId) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const member = await this.membersFetcher.patch([memberId, 'status'], {
            body: { status: MemberUpdateStatus.ACTIVE },
            parser: parserFromDto(MemberInfoResponseDto),
        });
        return await remapMember(member, adminCrypto);
    }
}
const remapMember = async ({ pendingInvitation, ...member }, adminCrypto) => {
    if (!pendingInvitation) {
        return member;
    }
    const remappedInvitation = await remapInvitationResponse(pendingInvitation, adminCrypto);
    return {
        ...member,
        pendingInvitation: remappedInvitation,
    };
};
